import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart3, TrendingUp, Users, Calendar, Target, Filter, Theater } from 'lucide-react';
import { Card, PageTitle } from '../ui';
import { useCrm } from '../store';
import { formatEUR } from '../utils';
import { useStructureFilter, STRUCTURE_LABELS } from '../structureContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend } from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SuiviDomaines from './SuiviDomaines';

const MOIS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
const COLORS = ['#f59e0b', '#10b981', '#0ea5e9', '#a855f7', '#ec4899', '#ef4444', '#14b8a6', '#84cc16'];

export default function StatsAvancees() {
  const data = useCrm();
  const { matches: matchesStructure, filter } = useStructureFilter();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'domaines' ? 'domaines' : 'vue-ensemble';

  const yearsAvailable = useMemo(() => {
    const set = new Set<number>();
    data.prest.forEach((p) => p.dateEvent && set.add(new Date(p.dateEvent).getFullYear()));
    data.factures?.forEach((f) => f.dateEmission && set.add(new Date(f.dateEmission).getFullYear()));
    if (set.size === 0) set.add(currentYear);
    return Array.from(set).sort((a, b) => b - a);
  }, [data.prest, data.factures, currentYear]);

  const prestYear = useMemo(
    () => data.prest.filter((p) => matchesStructure(p.structure) && p.dateEvent && new Date(p.dateEvent).getFullYear() === year),
    [data.prest, matchesStructure, year]
  );
  const facturesYear = useMemo(
    () => (data.factures || []).filter((f) => matchesStructure(f.structure) && f.dateEmission && new Date(f.dateEmission).getFullYear() === year),
    [data.factures, matchesStructure, year]
  );
  const devisYear = useMemo(
    () => (data.devis || []).filter((d) => matchesStructure(d.structure) && d.dateEmission && new Date(d.dateEmission).getFullYear() === year),
    [data.devis, matchesStructure, year]
  );

  // CA mensuel (factures payées + acomptes)
  const caMensuel = useMemo(() => {
    const arr = MOIS.map((m, i) => ({ mois: m, ca: 0, factures: 0, signed: 0 }));
    facturesYear.forEach((f) => {
      const m = new Date(f.dateEmission).getMonth();
      const ttc = f.lignes.reduce((s, l) => s + l.quantite * l.prixUnitaireHT * (1 + l.tvaPct / 100), 0) * (1 - (f.remisePct || 0) / 100);
      arr[m].ca += ttc;
      arr[m].factures += 1;
    });
    prestYear.filter((p) => p.statut === 'Signé').forEach((p) => {
      if (p.dateEvent) arr[new Date(p.dateEvent).getMonth()].signed += 1;
    });
    return arr;
  }, [facturesYear, prestYear]);

  // Top clients par CA
  const topClients = useMemo(() => {
    const map = new Map<string, { nom: string; ca: number; nb: number }>();
    facturesYear.forEach((f) => {
      const ttc = f.lignes.reduce((s, l) => s + l.quantite * l.prixUnitaireHT * (1 + l.tvaPct / 100), 0) * (1 - (f.remisePct || 0) / 100);
      const cur = map.get(f.clientNom) || { nom: f.clientNom, ca: 0, nb: 0 };
      cur.ca += ttc;
      cur.nb += 1;
      map.set(f.clientNom, cur);
    });
    return Array.from(map.values()).sort((a, b) => b.ca - a.ca).slice(0, 10);
  }, [facturesYear]);

  // Répartition par canal
  const parCanal = useMemo(() => {
    const map = new Map<string, number>();
    prestYear.forEach((p) => {
      const c = p.canal || 'Non renseigné';
      map.set(c, (map.get(c) || 0) + 1);
    });
    return Array.from(map.entries()).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [prestYear]);

  // Saisonnalité (volume événements par mois)
  const saisonnalite = useMemo(() => {
    const arr = MOIS.map((m) => ({ mois: m, evenements: 0 }));
    prestYear.forEach((p) => {
      if (p.dateEvent) arr[new Date(p.dateEvent).getMonth()].evenements += 1;
    });
    return arr;
  }, [prestYear]);

  // Taux de conversion devis → facture
  const conversion = useMemo(() => {
    const total = devisYear.length;
    const accept = devisYear.filter((d) => ['Accepté', 'Converti'].includes(d.statut)).length;
    const refus = devisYear.filter((d) => d.statut === 'Refusé').length;
    const enCours = devisYear.filter((d) => ['Brouillon', 'Envoyé', 'Vu', 'Relance'].includes(d.statut)).length;
    return {
      total,
      accept,
      refus,
      enCours,
      taux: total > 0 ? (accept / total) * 100 : 0,
    };
  }, [devisYear]);

  // Répartition par domaine
  const parDomaine = useMemo(() => {
    const map = new Map<string, number>();
    prestYear.forEach((p) => {
      (p.domaines || []).forEach((d) => map.set(d, (map.get(d) || 0) + 1));
    });
    return Array.from(map.entries()).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 8);
  }, [prestYear]);

  const caTotal = caMensuel.reduce((s, m) => s + m.ca, 0);
  const evenementsTotal = saisonnalite.reduce((s, m) => s + m.evenements, 0);
  const meilleurMois = caMensuel.reduce((best, m) => m.ca > best.ca ? m : best, caMensuel[0]);

  return (
    <div className="space-y-6">
      <PageTitle
        icon={<BarChart3 className="text-amber-400" size={24} strokeWidth={1.5} />}
        title="Stats avancées"
        subtitle={filter === 'all' ? 'Vue globale Cygnature + Suivez Vos Rêves' : `Filtré : ${STRUCTURE_LABELS[filter]}`}
        action={
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-slate-500" strokeWidth={1.5} />
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="px-3 py-2 bg-slate-900 border border-slate-800 rounded-md text-sm text-slate-200 focus:outline-none focus:border-amber-500/50"
            >
              {yearsAvailable.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        }
      />

      <Tabs
        value={tab}
        onValueChange={(v) => {
          const next = new URLSearchParams(searchParams);
          next.set('tab', v);
          setSearchParams(next, { replace: true });
        }}
      >
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="vue-ensemble" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-300">
            <BarChart3 size={14} strokeWidth={1.5} className="mr-2" /> Vue d'ensemble
          </TabsTrigger>
          <TabsTrigger value="domaines" className="data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-300">
            <Theater size={14} strokeWidth={1.5} className="mr-2" /> Suivi par domaine
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vue-ensemble" className="mt-4 space-y-6">
      {/* KPI synthèse */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-[10px] text-amber-400 uppercase tracking-widest font-semibold mb-1">
            <TrendingUp size={12} strokeWidth={1.5} /> CA {year}
          </div>
          <div className="text-2xl font-heading text-amber-300">{formatEUR(caTotal)}</div>
          <div className="text-[11px] text-slate-500 mt-1">{facturesYear.length} facture{facturesYear.length > 1 ? 's' : ''}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-[10px] text-emerald-400 uppercase tracking-widest font-semibold mb-1">
            <Calendar size={12} strokeWidth={1.5} /> Événements
          </div>
          <div className="text-2xl font-heading text-emerald-300">{evenementsTotal}</div>
          <div className="text-[11px] text-slate-500 mt-1">{prestYear.filter((p) => p.statut === 'Signé').length} signés</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-[10px] text-sky-400 uppercase tracking-widest font-semibold mb-1">
            <Target size={12} strokeWidth={1.5} /> Conversion devis
          </div>
          <div className="text-2xl font-heading text-sky-300">{conversion.taux.toFixed(0)}%</div>
          <div className="text-[11px] text-slate-500 mt-1">{conversion.accept} acc. / {conversion.total} émis</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-[10px] text-violet-400 uppercase tracking-widest font-semibold mb-1">
            <BarChart3 size={12} strokeWidth={1.5} /> Top mois
          </div>
          <div className="text-2xl font-heading text-violet-300">{meilleurMois?.mois || '—'}</div>
          <div className="text-[11px] text-slate-500 mt-1">{formatEUR(meilleurMois?.ca || 0)}</div>
        </Card>
      </div>

      {/* CA mensuel */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-widest text-amber-400">
          <TrendingUp size={14} strokeWidth={1.5} /> Chiffre d'affaires mensuel — {year}
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={caMensuel}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="mois" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }}
                formatter={(value: any) => [formatEUR(Number(value)), 'CA TTC']}
              />
              <Bar dataKey="ca" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Saisonnalité événements */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-widest text-emerald-400">
          <Calendar size={14} strokeWidth={1.5} /> Saisonnalité — Volume d'événements par mois
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={saisonnalite}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="mois" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} allowDecimals={false} />
              <Tooltip
                contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }}
                formatter={(value: any) => [`${value} événement${Number(value) > 1 ? 's' : ''}`, '']}
              />
              <Line type="monotone" dataKey="evenements" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Répartition canal */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-widest text-sky-400">
            <Users size={14} strokeWidth={1.5} /> Répartition par canal de contact
          </div>
          {parCanal.length === 0 ? (
            <div className="text-center text-slate-500 py-12 text-sm">Aucune donnée canal sur {year}</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={parCanal} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label={({ name, value }) => `${name} (${value})`} labelLine={false} fontSize={11}>
                    {parCanal.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        {/* Domaines */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-widest text-violet-400">
            <BarChart3 size={14} strokeWidth={1.5} /> Top 8 domaines artistiques
          </div>
          {parDomaine.length === 0 ? (
            <div className="text-center text-slate-500 py-12 text-sm">Aucune donnée domaine sur {year}</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={parDomaine} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis type="number" stroke="#64748b" fontSize={11} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" stroke="#64748b" fontSize={10} width={100} />
                  <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="value" fill="#a855f7" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* Top clients */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-widest text-amber-400">
          <Users size={14} strokeWidth={1.5} /> Top 10 clients par chiffre d'affaires — {year}
        </div>
        {topClients.length === 0 ? (
          <div className="text-center text-slate-500 py-12 text-sm">Aucune facture sur {year}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-800">
                  <th className="text-left py-2 px-3">#</th>
                  <th className="text-left py-2 px-3">Client</th>
                  <th className="text-right py-2 px-3">Factures</th>
                  <th className="text-right py-2 px-3">CA TTC</th>
                  <th className="text-right py-2 px-3">Part</th>
                </tr>
              </thead>
              <tbody>
                {topClients.map((c, i) => (
                  <tr key={c.nom} className="border-b border-slate-800/50 hover:bg-slate-900/50">
                    <td className="py-2.5 px-3 text-slate-500 font-mono text-xs">{i + 1}</td>
                    <td className="py-2.5 px-3 text-slate-200">{c.nom}</td>
                    <td className="py-2.5 px-3 text-right text-slate-400">{c.nb}</td>
                    <td className="py-2.5 px-3 text-right text-amber-300 font-medium">{formatEUR(c.ca)}</td>
                    <td className="py-2.5 px-3 text-right text-slate-500 text-xs">{caTotal > 0 ? ((c.ca / caTotal) * 100).toFixed(1) : '0'}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Conversion devis détail */}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-4 text-[10px] uppercase tracking-widest text-sky-400">
          <Target size={14} strokeWidth={1.5} /> Détail conversion devis — {year}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-slate-950 border border-slate-800 rounded-lg p-3">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Émis</div>
            <div className="text-xl font-heading text-slate-100 mt-1">{conversion.total}</div>
          </div>
          <div className="bg-slate-950 border border-emerald-500/30 rounded-lg p-3">
            <div className="text-[10px] text-emerald-400 uppercase tracking-wider">Acceptés</div>
            <div className="text-xl font-heading text-emerald-300 mt-1">{conversion.accept}</div>
          </div>
          <div className="bg-slate-950 border border-red-500/30 rounded-lg p-3">
            <div className="text-[10px] text-red-400 uppercase tracking-wider">Refusés</div>
            <div className="text-xl font-heading text-red-300 mt-1">{conversion.refus}</div>
          </div>
          <div className="bg-slate-950 border border-amber-500/30 rounded-lg p-3">
            <div className="text-[10px] text-amber-400 uppercase tracking-wider">En cours</div>
            <div className="text-xl font-heading text-amber-300 mt-1">{conversion.enCours}</div>
          </div>
        </div>
      </Card>
        </TabsContent>

        <TabsContent value="domaines" className="mt-4">
          <SuiviDomaines embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}
